// PeriodPicker — professional period selector with chips + calendar dropdown.
// Quick options: اليوم · أمس · الأسبوع الحالي · الأسبوع الماضي · الشهر الحالي · الشهر الماضي · كامل الفترة · مخصص
import React, { useEffect, useMemo, useRef, useState } from "react";
import { zVar } from "./lib/zIndex";
import { ksaAddDays, ksaParts, ksaStartOfMonth, ksaStartOfWeek, ksaToday } from "./lib/ksaTime";

const PRESETS = [
  { id: "today",     label: "اليوم",          icon: "📍" },
  { id: "yesterday", label: "أمس",            icon: "📅" },
  { id: "thisweek",  label: "الأسبوع الحالي", icon: "📆" },
  { id: "prevweek",  label: "الأسبوع الماضي", icon: "↩" },
  { id: "month",     label: "الشهر الحالي",   icon: "🗓" },
  { id: "prevmonth", label: "الشهر الماضي",   icon: "⏮" },
  { id: "all",       label: "كامل الفترة",    icon: "∞" },
  { id: "custom",    label: "مخصص",           icon: "🎯" },
];

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const AR_DOWS   = ["أحد", "اثن", "ثلا", "أرب", "خمي", "جمع", "سبت"];

// ✅ Local-time YYYY-MM-DD (NOT UTC). Saudi is +3, so toISOString shifts the day backward.
function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmt(s) {
  if (!s) return "—";
  // Parse YYYY-MM-DD manually to avoid Date() UTC interpretation surprises
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]); const mo = Number(m[2]); const da = Number(m[3]);
    return `${da} ${AR_MONTHS[mo - 1]} ${y}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const p = ksaParts(d);
  return `${p.date} ${AR_MONTHS[p.month - 1]} ${p.year}`;
}

// Compute the date range from a preset id (today's perspective)
export function rangeForPreset(id) {
  // ⑤/L3-أ — بتوقيت السعودية عبر lib/ksaTime.js، وبداية الأسبوع إعداد.
  const td = ksaToday();
  const yd = ksaAddDays(td, -1);
  const wkStart = ksaStartOfWeek(td);
  const prevWkEnd = ksaAddDays(wkStart, -1);
  const prevWkStart = ksaAddDays(wkStart, -7);
  const ms = ksaStartOfMonth(td);
  const prevMonthEnd = ksaAddDays(ms, -1);
  const prevMonthStart = ksaStartOfMonth(prevMonthEnd);
  switch (id) {
    case "today":     return { start: td, end: td };
    case "yesterday": return { start: yd, end: yd };
    case "thisweek":  return { start: wkStart, end: td };
    case "prevweek":  return { start: prevWkStart, end: prevWkEnd };
    case "month":     return { start: ms, end: td };
    case "prevmonth": return { start: prevMonthStart, end: prevMonthEnd };
    case "all":       return { start: "", end: "" };
    default:          return { start: "", end: "" };
  }
}

export default function PeriodPicker({ period, dateFrom, dateTo, onChange }) {
  const [open, setOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(() => {
    const [by, bm] = String(dateFrom || ksaToday()).slice(0, 7).split("-").map(Number);
    return new Date(by, bm - 1, 1);
  });
  const [pickStart, setPickStart] = useState(dateFrom || "");
  const [pickEnd, setPickEnd]   = useState(dateTo || "");
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Current preset label
  const currentPreset = PRESETS.find((p) => p.id === period) || PRESETS[6]; // default "all"
  const range = useMemo(() => {
    if (period === "custom") return { start: dateFrom, end: dateTo };
    return rangeForPreset(period);
  }, [period, dateFrom, dateTo]);

  const subtitle = useMemo(() => {
    if (period === "all") return "كل البيانات المتاحة";
    if (!range.start) return "—";
    if (range.start === range.end) return fmt(range.start);
    return `${fmt(range.start)} ← ${fmt(range.end)}`;
  }, [period, range]);

  const pickPreset = (id) => {
    if (id === "custom") {
      // Don't apply yet — keep panel open so user can pick a range via the calendar.
      // The actual apply happens when they click "تطبيق".
      return;
    }
    onChange({ period: id });
    setOpen(false);
  };

  // ─── Calendar logic ───
  const monthDays = useMemo(() => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const offset = first.getDay(); // 0=Sun
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(y, m, d));
    return days;
  }, [calMonth]);

  const handleDayClick = (d) => {
    if (!d) return;
    const s = iso(d);
    if (!pickStart || (pickStart && pickEnd)) {
      setPickStart(s);
      setPickEnd("");
    } else if (s < pickStart) {
      setPickEnd(pickStart);
      setPickStart(s);
    } else {
      setPickEnd(s);
    }
  };

  const applyCustom = () => {
    if (!pickStart) return;
    const end = pickEnd || pickStart;
    onChange({ period: "custom", dateFrom: pickStart, dateTo: end });
    setOpen(false);
  };

  const inRange = (d) => {
    if (!d || !pickStart) return false;
    const s = iso(d);
    if (!pickEnd) return s === pickStart;
    return s >= pickStart && s <= pickEnd;
  };
  const isStartOrEnd = (d) => {
    if (!d) return false;
    const s = iso(d);
    return s === pickStart || s === pickEnd;
  };
  const isToday = (d) => d && iso(d) === ksaToday();

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }} data-testid="period-picker-root">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="period-picker-trigger"
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "8px 14px", borderRadius: 10, cursor: "pointer",
          background: open ? "var(--brown)" : "var(--beige)",
          color: open ? "#fff" : "var(--brown-deep)",
          border: `1.5px solid ${open ? "var(--brown)" : "var(--border)"}`,
          fontSize: 13, fontWeight: 700, fontFamily: "var(--f-n)",
          transition: "all 160ms ease",
          minWidth: 220, justifyContent: "space-between",
        }}
      >
        <span>{currentPreset.icon} {currentPreset.label}</span>
        <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 500 }}>{subtitle}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div data-testid="period-picker-panel" style={{
          position: "absolute", top: "calc(100% + 6px)", insetInlineStart: 0, zIndex: zVar('extra_1000'),
          background: "var(--card)", borderRadius: 12, boxShadow: "0 10px 32px rgba(0,0,0,0.18)",
          border: "1px solid var(--border)", overflow: "hidden",
          display: "flex", flexDirection: "row", minWidth: 580,
        }}>
          {/* Presets column */}
          <div style={{ padding: 12, borderInlineEnd: "1px solid var(--border)", background: "var(--beige)", minWidth: 180 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, marginBottom: 8 }}>الفترة السريعة</div>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPreset(p.id)}
                data-testid={`period-preset-${p.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "8px 12px", marginBottom: 4, borderRadius: 6,
                  background: period === p.id ? "var(--brown)" : "transparent",
                  color: period === p.id ? "#fff" : "var(--brown-deep)",
                  border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                  textAlign: "start",
                  transition: "background 120ms ease",
                }}
                onMouseEnter={(e) => { if (period !== p.id) e.currentTarget.style.background = "rgba(107,78,61,0.08)"; }}
                onMouseLeave={(e) => { if (period !== p.id) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 14 }}>{p.icon}</span>
                <span>{p.label}</span>
              </button>
            ))}
          </div>

          {/* Calendar column */}
          <div style={{ padding: 14, minWidth: 320 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <button type="button" data-testid="cal-prev"
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}
                style={navBtn}>‹</button>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)" }}>
                {AR_MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}
              </div>
              <button type="button" data-testid="cal-next"
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}
                style={navBtn}>›</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 6 }}>
              {AR_DOWS.map((d) => (
                <div key={d} style={{ fontSize: 10, color: "var(--muted)", textAlign: "center", padding: 4, fontWeight: 700 }}>{d}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {monthDays.map((d, i) => {
                if (!d) return <div key={`e-${i}`} />;
                const sel = isStartOrEnd(d);
                const ir = inRange(d);
                const tod = isToday(d);
                return (
                  <button
                    key={iso(d)}
                    type="button"
                    onClick={() => handleDayClick(d)}
                    data-testid={`cal-day-${iso(d)}`}
                    style={{
                      padding: "6px 0", borderRadius: 6, cursor: "pointer",
                      background: sel ? "var(--brown)" : ir ? "rgba(107,78,61,0.15)" : "transparent",
                      color: sel ? "#fff" : "var(--brown-deep)",
                      border: tod && !sel ? "1.5px solid var(--gold)" : "1px solid transparent",
                      fontSize: 12, fontWeight: sel ? 700 : 500, fontFamily: "var(--f-n)",
                      transition: "all 120ms ease",
                    }}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Selected range display + apply */}
            <div style={{
              marginTop: 12, padding: 10, background: "var(--beige)", borderRadius: 8,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
            }}>
              <div style={{ fontSize: 11.5, color: "var(--brown-deep)", flex: 1 }}>
                {pickStart ? (
                  pickEnd && pickEnd !== pickStart
                    ? <span><b>{fmt(pickStart)}</b> ← <b>{fmt(pickEnd)}</b></span>
                    : <span>اختر تاريخ النهاية — البداية: <b>{fmt(pickStart)}</b></span>
                ) : <span style={{ color: "var(--muted)" }}>اضغط على يوم لبدء التحديد</span>}
              </div>
              <button
                type="button"
                onClick={applyCustom}
                disabled={!pickStart}
                data-testid="period-apply-custom"
                style={{
                  padding: "6px 14px", borderRadius: 6, cursor: pickStart ? "pointer" : "not-allowed",
                  background: pickStart ? "var(--success)" : "var(--muted)",
                  color: "#fff", border: "none", fontSize: 12, fontWeight: 700,
                }}
              >
                تطبيق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  width: 30, height: 30, borderRadius: 6, cursor: "pointer",
  background: "var(--beige)", color: "var(--brown-deep)",
  border: "1px solid var(--border)", fontSize: 16, fontWeight: 700,
};
